import React, { useEffect, Fragment } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { BsChevronLeft } from "react-icons/bs";
import Footer from "../Components/Reuseable/Footer.jsx";
import CoptRight from "../Components/Reuseable/Copyrights";
import HorseDetail from "../Components/Modal/HorseModal";

/**
 * HorseDetailPage
 * ─────────────────────────────────────────────
 * A full standalone page for a single Horse.
 * URL patterns supported:
 *   /horse-detail?<id>          (query-string style, same as /race-detail)
 *   /horse-detail/:id           (param style, optional)
 *
 * The page renders:
 *  1. A common-page-banner with the horse name + "Details" heading
 *  2. A white container with a dark top-header-bar (back-arrow + horse name)
 *  3. The existing HorseDetail modal component embedded directly (not in a popup)
 */
const HorseDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();
  const cookiedata = Cookies.get("i18next");
  const dispatch = useDispatch();

  // Accept both URL patterns: /horse-detail?<id>  or  /horse-detail/:id
  const searchParam = new URLSearchParams(location.search).get("id");
  const rawQuery = location.search.startsWith("?") ? location.search.slice(1) : location.search;
  const id = paramId || searchParam || (rawQuery.startsWith("=") ? rawQuery.slice(1) : rawQuery) || location.state?.id;

  const { data: singlehorse, status } = useSelector(
    (state) => state.singlehorse
  );

  useEffect(() => {
    // Scroll to top on load
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const horseName =
    singlehorse && status !== "loading"
      ? cookiedata === "en"
        ? singlehorse.NameEn
          ? (window.textTransform ? window.textTransform(singlehorse.NameEn) : singlehorse.NameEn)
          : ""
        : singlehorse.NameAr || singlehorse.NameEn || ""
      : "";

  const bannerTitle = horseName 
    ? horseName 
    : t("Horse");

  return (
    <Fragment>
      {/* ── Banner (same class used by RaceCardDetailNew) ── */}
      <div className="common-page-banner">
        <h1>{bannerTitle}</h1>
      </div>

      <div className="container-fluid new-main-wrapper">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div
                className="new-race-detail-container col-sm-12"
                dir={cookiedata?.startsWith("ar") ? "rtl" : "ltr"}
                style={{ background: "rgb(255,255,255)", borderRadius: "10px" }}
              >
                {/* ── Dark top header bar (back arrow + name) ── */}
                <div
                  className="new-race-top-header-bar"
                  style={{ background: "rgb(10 31 67)" }}
                >
                  <div className="new-header-left-side">
                    <div
                      className="new-header-title-group"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                      }}
                    >
                      <BsChevronLeft
                        className="new-back-arrow"
                        onClick={() => navigate(-1)}
                        style={{ cursor: "pointer" }}
                      />
                      <h2 className="new-header-track-name">{horseName || t("Loading...")}</h2>
                    </div>
                  </div>
                </div>

                {/* ── Main horse detail content (the existing HorseModal) ── */}
                {id ? (
                  <div style={{ padding: "10px 0" }}>
                    {/* HorseModal: component param 'data' is the props object.
                        It reads data.data._id → so we pass data prop = { _id: id } */}
                    <HorseDetail data={{ _id: id }} />
                  </div>
                ) : (
                  <div className="py-5 text-center">
                    <p style={{ color: "#888", fontSize: "18px" }}>
                      {t("No horse selected.")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <CoptRight />
    </Fragment>
  );
};

export default HorseDetailPage;
